import { useEffect } from 'react';
import { SubscriptionRedirected } from './type';
import Bowser from 'bowser';
import { getXsollaToken } from './api';
import { useGetXsollaPaymentWidget } from './useGetXsollaPaymentWidget';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { XSOLLA_POPUP_OPEN_DELAY_MS } from '@wwe-portal/web/utils/constants/offer';
import { XsollaPaymentError } from '@wwe-portal/web/libs/xsolla/type';

export interface XsollaPaymentInfo {
  status: string;
  foreignInvoice: string;
  invoice_id: string;
}

export const getXsollaVersion = () => {
  const body = document.getElementsByTagName('body')[0];
  return body?.clientWidth && body.clientWidth > 768 ? 'desktop' : 'mobile';
};

const clearPaymentParams = (search: string, keys: string[]) => {
  const params = new URLSearchParams(search);
  keys.forEach((key) => params.delete(key));
  return params.toString();
};
const extractPaymentParams = (search: string, keys: string[]) => {
  const params = new URLSearchParams(search);
  const result = Array.from(params.keys()).reduce((result, key) => {
    const values = params.getAll(key);

    const originKey = key.replace(/\[\d+\]/, '');
    if (!keys.includes(originKey)) return result;
    if (!values || values.length === 0) return result;
    return {
      ...result,
      [key]: params.get(key),
    };
  }, {}) as XsollaPaymentInfo;

  if (Object.keys(result).length >= keys.length) {
    return result as unknown as XsollaPaymentInfo;
  }
  return null;
};

const delayRedirectionReject = (reject: (reason?: unknown) => void) => {
  setTimeout(() => {
    reject(new SubscriptionRedirected());
  }, XSOLLA_POPUP_OPEN_DELAY_MS);
};

const enableGlobalScrollbar = () => {
  setTimeout(() => {
    document.body.style.overflow = 'auto';
  });
};

export interface UsePurchaseXsollaPaymentParameter<T> {
  getData: () => T | null;
  persistData: (nextData: T | null) => void;
  clearData: () => void;
  onPurchaseSuccess: ({
    data,
    paymentInfo,
  }: {
    data: T;
    paymentInfo: XsollaPaymentInfo;
  }) => void;
  onWidgetLoaded?: () => void;
  onWidgetClosed?: (plan?: T) => void;
}

export const usePurchaseXsollaPayment = <T>({
  persistData,
  getData,
  clearData,
  onPurchaseSuccess,
  onWidgetLoaded,
  onWidgetClosed,
}: UsePurchaseXsollaPaymentParameter<T>) => {
  const searchUrl = useSearchParams();
  const pathname = usePathname();
  const search = searchUrl.toString();
  const history = useRouter();
  const { isMobile } = useResponsive();
  const getXsollaPaymentWidget = useGetXsollaPaymentWidget();

  const openXsollaPopupOrWindow = (
    access_token: string,
    onRedirect?: () => void
  ) => {
    const { xsollaUrl } = getXsollaPaymentWidget(access_token);

    const browser = Bowser.getParser(window.navigator.userAgent);
    if (
      isMobile ||
      browser.satisfies({
        tablet: {
          safari: '<17.0',
        },
      })
    ) {
      window.location.href = xsollaUrl;
      onRedirect?.();
    } else {
      window.XPayStationWidget.open();
    }
  };

  const startPurchase = async ({ token, data }: { token: string; data: T }) => {
    persistData(data);

    const paymentInfo = await new Promise<XsollaPaymentInfo | undefined>(
      (resolve, reject) => {
        let isSuccess = false;
        let isError = false;
        let isLoaded = false;
        let currentPaymentInfo: XsollaPaymentInfo | undefined = undefined;

        window.XPayStationWidget.on(
          window.XPayStationWidget.eventTypes.LOAD,
          function () {
            isLoaded = true;
            onWidgetLoaded?.();
          }
        );

        window.XPayStationWidget.on(
          window.XPayStationWidget.eventTypes.STATUS_DONE,
          function (_: unknown, data: unknown) {
            isSuccess = true;
            currentPaymentInfo = data as XsollaPaymentInfo;
          }
        );

        window.XPayStationWidget.on(
          window.XPayStationWidget.eventTypes.STATUS_TROUBLED,
          function () {
            isError = true;
          }
        );

        window.XPayStationWidget.on(
          window.XPayStationWidget.eventTypes.CLOSE,
          () => {
            // Sometimes, closing xsolla widget will disable global scroll, this is a workaround of xsolla issue
            enableGlobalScrollbar();
            onWidgetClosed?.(data);
            window.XPayStationWidget.off();

            if (isSuccess) {
              resolve(currentPaymentInfo);
              return;
            }

            if (isError) {
              reject(new XsollaPaymentError('failed'));
              return;
            }

            if (!isLoaded) {
              reject(new XsollaPaymentError('closed'));
            }

            reject(new XsollaPaymentError('cancelled'));
          }
        );

        const { options } = getXsollaPaymentWidget(token);
        window.XPayStationWidget.init(options);
        openXsollaPopupOrWindow(token, () => {
          delayRedirectionReject(reject);
        });
      }
    );

    window.XPayStationWidget.off();
    return paymentInfo;
  };

  useEffect(() => {
    const paymentUrlParams = extractPaymentParams(search, [
      'status',
      'foreignInvoice',
      'invoice_id',
    ]);

    if (!paymentUrlParams || paymentUrlParams.status !== 'done') {
      clearData();
      return;
    }
    const currentData = getData();

    if (!currentData) return;

    const paymentInfo = paymentUrlParams;
    if (paymentInfo) {
      onPurchaseSuccess({ paymentInfo, data: currentData });
      const nextParams = clearPaymentParams(search, [
        'status',
        'foreignInvoice',
        'invoice_id',
        'user_id',
      ]);

      history.push(`${pathname}?${nextParams.toString()}`);
    }

    clearData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, search]);
  return { startPurchase };
};

export const usePurchaseOfferXsollaPayment = <T>({
  persistData,
  getData,
  clearData,
  onPurchaseSuccess,
  onWidgetLoaded,
  onWidgetClosed,
}: {
  getData: () => T | null;
  persistData: (nextData: T | null) => void;
  clearData: () => void;
  onPurchaseSuccess: ({
    data,
    paymentInfo,
  }: {
    data: T;
    paymentInfo: XsollaPaymentInfo;
  }) => void;
  onWidgetClosed?: () => void;
  onWidgetLoaded?: () => void;
}) => {
  const { startPurchase } = usePurchaseXsollaPayment({
    persistData,
    getData,
    clearData,
    onPurchaseSuccess,
    onWidgetLoaded,
    onWidgetClosed,
  });

  const startOfferPurchase = async ({
    offerData,
    data,
  }: {
    offerData: OfferData;
    data: T;
  }) => {
    const response = await getXsollaToken({
      sku: offerData.XsollaID,
      offerId: offerData.OffersID,
      packageId: offerData.HardCurrencyPackageID,
      redirectUri: window.location.href,
      version: getXsollaVersion(),
    });
    const token = response.data.data.token;
    return startPurchase({ token, data });
  };

  return { startOfferPurchase };
};
